/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace binomial
*/
var binomial = {};

/**
* @name cdf
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/cdf}
*/
import cdf from './../../../../base/dists/binomial/cdf';
setReadOnly( binomial, 'cdf', cdf );

/**
* @name Binomial
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/ctor}
*/
import Binomial from './../../../../base/dists/binomial/ctor';
setReadOnly( binomial, 'Binomial', Binomial );

/**
* @name entropy
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/entropy}
*/
import entropy from './../../../../base/dists/binomial/entropy';
setReadOnly( binomial, 'entropy', entropy );

/**
* @name kurtosis
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/kurtosis}
*/
import kurtosis from './../../../../base/dists/binomial/kurtosis';
setReadOnly( binomial, 'kurtosis', kurtosis );

/**
* @name logpmf
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/logpmf}
*/
import logpmf from './../../../../base/dists/binomial/logpmf';
setReadOnly( binomial, 'logpmf', logpmf );

/**
* @name mean
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/mean}
*/
import mean from './../../../../base/dists/binomial/mean';
setReadOnly( binomial, 'mean', mean );

/**
* @name median
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/median}
*/
import median from './../../../../base/dists/binomial/median';
setReadOnly( binomial, 'median', median );

/**
* @name mgf
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/mgf}
*/
import mgf from './../../../../base/dists/binomial/mgf';
setReadOnly( binomial, 'mgf', mgf );

/**
* @name mode
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/mode}
*/
import mode from './../../../../base/dists/binomial/mode';
setReadOnly( binomial, 'mode', mode );

/**
* @name pmf
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/pmf}
*/
import pmf from './../../../../base/dists/binomial/pmf';
setReadOnly( binomial, 'pmf', pmf );

/**
* @name quantile
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/quantile}
*/
import quantile from './../../../../base/dists/binomial/quantile';
setReadOnly( binomial, 'quantile', quantile );

/**
* @name skewness
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/skewness}
*/
import skewness from './../../../../base/dists/binomial/skewness';
setReadOnly( binomial, 'skewness', skewness );

/**
* @name stdev
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/stdev}
*/
import stdev from './../../../../base/dists/binomial/stdev';
setReadOnly( binomial, 'stdev', stdev );

/**
* @name variance
* @memberof binomial
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/binomial/variance}
*/
import variance from './../../../../base/dists/binomial/variance';
setReadOnly( binomial, 'variance', variance );


// EXPORTS //

export default binomial;
